import { TRANSLATED_ACTIONS } from '@/common/messages';
import type { DialogProps } from '@/components/dialog/useDialogWithExtraProps';
import { Dropzone, mapToRcFiles } from '@/components/files/dropzone';
import { TextArea } from '@/components/form';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import { Modal, type StyledModalProps } from '@/components/modal/Modal';
import { Typography } from '@/components/typography';
import { HeadlineLevel } from '@/components/typography/enums/level';
import { useCustomerCurrency } from '@/containers/App';
import { fileActions } from '@/containers/App/actions';
import { useBugReport } from '@/services/hooks/support/useBugReport';
import type { StrictOmit } from '@enmon/commons';
import type { BugReportErrorDetails, BugReportContext } from '@enmon/commons/lib/enmon';
import type { UploadFile } from 'antd/lib/upload';
import { useState, type VFC } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Flex } from '../layouting';
import { BugReport as messages } from './messages';
import { APP_DICTIONARY } from '@/common/messages/root';

const { Headline, Paragraph } = Typography;

export interface BugReportModalProps extends StrictOmit<StyledModalProps, 'onCancel'>, DialogProps {
  errorDetails?: BugReportErrorDetails;
}

export const BugReportModal: VFC<BugReportModalProps> = ({ onClose, isHidden, errorDetails, ...modalProps }) => {
  const dispatch = useDispatch();
  const [reportText, setReportText] = useState('');
  const [files, setFiles] = useState<UploadFile[]>([]);

  const intl = useIntl();
  const location = useLocation();
  const customerCurrency = useCustomerCurrency();

  const { mutate: reportBug, isLoading: isLoadingBugReport } = useBugReport(onClose);

  const maxFileCount = 5;

  const handleSubmitFiles = (newFiles: UploadFile[]) => {
    if (newFiles.length > maxFileCount) {
      dispatch(fileActions.fileLimit.error());
      return;
    }
    setFiles(mapToRcFiles(newFiles));
  };

  const handleSendBugReport = () => {
    const context: BugReportContext = {
      customerCurrency,
      url: `${window.location.origin}${location.pathname}${location.search}`,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      errorDetails,
    };

    reportBug({
      message: reportText,
      attachments: mapToRcFiles(files),
      context,
    });
  };

  return (
    <Modal
      confirmLoading={isLoadingBugReport}
      maxWidth={600}
      okButtonProps={{ disabled: !reportText }}
      okText={<FormattedMessage {...TRANSLATED_ACTIONS.report} />}
      open={!isHidden}
      title={<FormattedMessage {...messages.title} />}
      onCancel={onClose}
      onOk={handleSendBugReport}
      {...modalProps}
    >
      <Flex vertical gap={16}>
        <Flex vertical gap={8}>
          <Paragraph>
            <FormattedMessage {...messages.description} /> <span style={{ color: 'red' }}>*</span>
          </Paragraph>
          <TextArea
            required
            placeholder={intl.formatMessage(messages.placeholder)}
            rows={6}
            value={reportText}
            onChange={e => setReportText(e.target.value)}
          />
        </Flex>

        <Flex vertical gap={8}>
          <Headline level={HeadlineLevel.Three}>
            <FormattedMessage {...APP_DICTIONARY.attachments} />
          </Headline>
          <Dropzone multiple fileList={files} maxCount={maxFileCount} onChange={handleSubmitFiles} />
          <Paragraph>
            <FormattedMessage values={{ limit: maxFileCount }} {...messages.fileLimit} />
          </Paragraph>
        </Flex>
      </Flex>
    </Modal>
  );
};
